import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Derive 1–2 initials from a name. MASTER_PROMPT spec: "All avatars show
 * initials, never '?'." So the fallback is bullet "•", not "?". Callers
 * who have an email should prefer `avatarInitials(name, email)` below
 * which falls back to the email local-part before giving up.
 */
export function initials(name: string | null | undefined, fallback = "•"): string {
  if (!name) return fallback;
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return fallback;
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/**
 * Smarter initials — try the name first, then fall back to first 1–2
 * letters of the email local-part, then "•". Never returns "?".
 */
export function avatarInitials(name?: string | null, email?: string | null): string {
  const n = (name || "").trim();
  if (n) {
    const parts = n.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
    return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
  }
  const e = (email || "").trim();
  if (e) {
    const local = e.split("@")[0]!;
    if (/^[a-z]/i.test(local)) return local.slice(0, 2).toUpperCase();
  }
  return "•";
}

/**
 * Convert a vertical key like "property_mgmt" / "PEST_CONTROL" /
 * "real-estate" into a sentence-case display label ("Property mgmt",
 * "Pest control", "Real estate"). Lookup table handles common
 * abbreviations (RIA, CPA, MSP, 3PL, etc.) so they stay capitalized.
 *
 * MASTER_PROMPT spec: verticals render in sentence case across the CRM —
 * not SCREAMING_SNAKE_CASE.
 */
const VERTICAL_LOOKUP: Record<string, string> = {
  ria: "RIA",
  cpa: "CPA",
  msp: "MSP",
  hoa: "HOA",
  hvac: "HVAC",
  "3pl": "3PL",
  saas: "SaaS",
  b2b: "B2B",
  b2c: "B2C",
  imaging_labs: "Imaging labs",
  property_mgmt: "Property mgmt",
  pest_control: "Pest control",
  home_health: "Home health",
  real_estate: "Real estate",
  law_firm: "Law firm",
  credit_union: "Credit union",
  mortgage_broker: "Mortgage broker",
  title_escrow: "Title / escrow",
  physical_therapy: "Physical therapy",
  immigration_law: "Immigration law",
  senior_care: "Senior care",
};
export function verticalLabel(v?: string | null): string {
  if (!v) return "";
  const k = v.toLowerCase();
  if (VERTICAL_LOOKUP[k]) return VERTICAL_LOOKUP[k];
  return v
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/^(\w)/, (m) => m.toUpperCase())
    .trim();
}

const COLORS = [
  "bg-cyan-100 text-cyan-900",
  "bg-violet-100 text-violet-900",
  "bg-emerald-100 text-emerald-900",
  "bg-amber-100 text-amber-900",
  "bg-rose-100 text-rose-900",
  "bg-sky-100 text-sky-900",
  "bg-fuchsia-100 text-fuchsia-900",
  "bg-teal-100 text-teal-900",
];

export function avatarColor(seed: string | null | undefined): string {
  if (!seed) return COLORS[0]!;
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  return COLORS[Math.abs(hash) % COLORS.length]!;
}
